import {CSSProperties, Fragment, ReactNode} from 'react'
import clsx from 'clsx'
import CommonPagination from '../_metronic/helpers/components/CommonPagination'
import {TruncatedText} from './Tooltip'

type RowKey = string | number

export interface TableColumn<T> {
  id: string
  header: ReactNode
  /** Cell content for a row. `index` is the row's position in `data`. */
  cell: (row: T, index: number) => ReactNode
  /**
   * Column width. A px value (120 or '120px') fixes it. Omit it (or 'auto') for a flexible
   * column: flexible columns split the space left by the fixed ones equally and shrink
   * with the screen. A percentage ('20%') is also flexible, but takes that share of the table.
   *
   * Only fix small columns whose content has a known size (checkbox, S.No, toggles,
   * action icons). Leave text columns flexible — if just one or two text columns are
   * flexible they soak up all the spare width and leave big gaps on wide screens.
   */
  width?: number | string
  /**
   * Smallest width (px) a flexible column may shrink to before the table scrolls
   * horizontally inside its wrapper. Defaults to 100.
   */
  minWidth?: number
  align?: 'start' | 'center' | 'end'
  /**
   * Truncate plain text/number cell values to one line with an ellipsis and show the
   * full value in a tooltip when cut off. For custom markup use TruncatedText in `cell`.
   */
  truncate?: boolean
  headerClassName?: string
  /** Static class, or a per-row class (e.g. to highlight the edge cells of an expanded row). */
  cellClassName?: string | ((row: T, index: number) => string | undefined)
}

export interface TableSelection<K extends RowKey = RowKey> {
  selected: K[]
  onChange: (selected: K[]) => void
}

export interface TablePagination {
  page: number
  limit: number
  totalItems: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  /** Label in "Showing 1-10 of 42 {title}". */
  title?: string
}

interface TableProps<T, K extends RowKey = RowKey> {
  columns: TableColumn<T>[]
  data: T[]
  rowKey: (row: T) => K
  id?: string
  /** Adds a leading checkbox column; the header checkbox selects every row in `data`. */
  selection?: TableSelection<K>
  /** With `selection`: rows returning false get a disabled checkbox and are skipped by select-all. */
  isRowSelectable?: (row: T) => boolean
  pagination?: TablePagination
  loading?: boolean
  emptyMessage?: ReactNode
  /** Extra full-width row rendered under a row, e.g. an expanded detail panel. */
  renderRowExpansion?: (row: T, index: number) => ReactNode
  rowClassName?: (row: T, index: number) => string | undefined
  rowStyle?: (row: T, index: number) => CSSProperties | undefined
  className?: string
}

const SELECTION_COLUMN_WIDTH = 50
const DEFAULT_FLEXIBLE_MIN_WIDTH = 100

// Heading colors from /allProducts/list (`#kt_table_amazon_accounts th` in
// keenicons/solid/style.css). Applied inline because that rule is scoped to the list
// table's id, which this shared table can't reuse.
const HEADER_CELL_STYLE: CSSProperties = {
  background: 'var(--table-head-bg)',
  color: 'var(--table-head-color)',
}

// Metronic's `.table` zeroes the first/last cell's side padding, and the card body
// has none either, so edge cells would touch the card border. Pad them instead.
// Fixed-width edge columns grow by this padding so their content keeps the width they asked for.
const EDGE_CELL_PADDING = 20

const edgeExtraWidth = (position: number, total: number) =>
  (position === 0 ? EDGE_CELL_PADDING : 0) + (position === total - 1 ? EDGE_CELL_PADDING : 0)

const edgeCellStyle = (position: number, total: number): CSSProperties => ({
  ...(position === 0 ? {paddingLeft: EDGE_CELL_PADDING} : undefined),
  ...(position === total - 1 ? {paddingRight: EDGE_CELL_PADDING} : undefined),
})

const toCssWidth = (width?: number | string) =>
  width === undefined ? 'auto' : typeof width === 'number' ? `${width}px` : width

// Only px widths are fixed; 'auto', percentages and other units adapt to the table width.
const isFlexible = (width?: number | string) =>
  width === undefined || (typeof width === 'string' && !/^\d+(\.\d+)?px$/.test(width.trim()))

const alignClass = (align?: TableColumn<any>['align']) =>
  align === 'center' ? 'text-center' : align === 'end' ? 'text-end' : undefined

const LoadingOverlay = () => {
  const style: CSSProperties = {
    position: 'absolute',
    textAlign: 'center',
    borderRadius: '0.475rem',
    boxShadow: '0 0 50px 0 rgb(82 63 105 / 15%)',
    backgroundColor: '#fff',
    color: '#7e8299',
    fontWeight: 500,
    padding: '1rem 2rem',
    top: 'calc(50% - 2rem)',
    left: 'calc(50% - 4rem)',
  }
  return <div style={style}>Processing...</div>
}

/**
 * Reusable data table with the same layout and responsive behavior as /allProducts/list:
 *
 * - `table-layout: fixed` with a <colgroup>: fixed columns keep their width, flexible
 *   ones share what's left, so long content can never push the table wider. On
 *   /allProducts/list the flexible columns are Product / SKU and Status (the long-text
 *   ones); size fixed columns to their content and keep text columns flexible.
 * - The table has a min width (fixed widths + each flexible column's minWidth). Above it
 *   the table fits the card with no horizontal scroll; below it, it scrolls inside its
 *   own `.table-responsive` wrapper so every column, including actions, stays usable.
 * - `truncate` columns ellipsize long values and show the full text in a tooltip.
 * - Optional checkbox selection and CommonPagination, matching the list page.
 *
 * Render it inside `<KTCardBody className='py-4 set_card_padding_right_left'>` so the
 * table borders meet the card edges, as on /allProducts/list.
 */
const Table = <T, K extends RowKey = RowKey>({
  columns,
  data,
  rowKey,
  id,
  selection,
  isRowSelectable,
  pagination,
  loading = false,
  emptyMessage = 'No matching records found',
  renderRowExpansion,
  rowClassName,
  rowStyle,
  className,
}: TableProps<T, K>) => {
  const totalColumns = columns.length + (selection ? 1 : 0)
  // Position of a data column among all rendered columns (the selection column comes first)
  const positionOf = (colIndex: number) => colIndex + (selection ? 1 : 0)
  const selectionWidth = SELECTION_COLUMN_WIDTH + edgeExtraWidth(0, totalColumns)
  const columnWidth = (col: TableColumn<T>, colIndex: number) =>
    isFlexible(col.width)
      ? toCssWidth(col.width)
      : `${(parseFloat(toCssWidth(col.width)) || 0) + edgeExtraWidth(positionOf(colIndex), totalColumns)}px`
  const minWidth =
    (selection ? selectionWidth : 0) +
    columns.reduce(
      (sum, col, colIndex) =>
        sum +
        (isFlexible(col.width)
          ? (col.minWidth ?? DEFAULT_FLEXIBLE_MIN_WIDTH) + edgeExtraWidth(positionOf(colIndex), totalColumns)
          : parseFloat(columnWidth(col, colIndex))),
      0
    )

  const rowKeys = data.map(rowKey)
  const selectableKeys = data
    .filter((row) => !isRowSelectable || isRowSelectable(row))
    .map(rowKey)
  const isAllSelected =
    !!selection &&
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selection.selected.includes(key))

  const toggleAll = () => {
    if (!selection) return
    selection.onChange(isAllSelected ? [] : selectableKeys)
  }

  const toggleRow = (key: K) => {
    if (!selection) return
    const {selected, onChange} = selection
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key])
  }

  const renderCell = (col: TableColumn<T>, row: T, index: number) => {
    const value = col.cell(row, index)
    if (col.truncate && (typeof value === 'string' || typeof value === 'number')) {
      return <TruncatedText text={String(value)} />
    }
    return value
  }

  return (
    <>
      {/* The overlay sits outside .table-responsive: its overflow would clip it while the
          table is short (e.g. only the header during the first load) */}
      <div className='position-relative'>
        <div className='table-responsive mb-10'>
          <table
            id={id}
            className={clsx(
              'table align-middle table-bordered table-row-dashed table-layout-fixed fs-6 gy-5 dataTable no-footer rounded',
              className
            )}
            style={{width: '100%', minWidth}}
          >
            <colgroup>
              {selection && <col style={{width: `${selectionWidth}px`}} />}
              {columns.map((col, colIndex) => (
                <col key={col.id} style={{width: columnWidth(col, colIndex)}} />
              ))}
            </colgroup>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                {selection && (
                  <th className='pe-2' style={{...HEADER_CELL_STYLE, ...edgeCellStyle(0, totalColumns)}}>
                    <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
                      <input
                        style={{cursor: 'pointer'}}
                        className='form-check-input'
                        type='checkbox'
                        checked={isAllSelected}
                        disabled={selectableKeys.length === 0}
                        onChange={toggleAll}
                        aria-label='Select all rows'
                      />
                    </div>
                  </th>
                )}
                {columns.map((col, colIndex) => (
                  <th
                    key={col.id}
                    className={clsx(alignClass(col.align), col.headerClassName)}
                    style={{...HEADER_CELL_STYLE, ...edgeCellStyle(positionOf(colIndex), totalColumns)}}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='text-gray-600'>
              {data.length > 0 ? (
                data.map((row, index) => {
                  const key = rowKeys[index]
                  const expansion = renderRowExpansion?.(row, index)
                  return (
                    <Fragment key={key}>
                      <tr className={rowClassName?.(row, index)} style={rowStyle?.(row, index)}>
                        {selection && (
                          <td style={edgeCellStyle(0, totalColumns)}>
                            <div className='form-check form-check-custom form-check-solid'>
                              <input
                                style={{cursor: 'pointer'}}
                                className='form-check-input'
                                type='checkbox'
                                checked={selection.selected.includes(key)}
                                disabled={!!isRowSelectable && !isRowSelectable(row)}
                                onChange={() => toggleRow(key)}
                                aria-label='Select row'
                              />
                            </div>
                          </td>
                        )}
                        {columns.map((col, colIndex) => (
                          <td
                            key={col.id}
                            className={clsx(
                              alignClass(col.align),
                              typeof col.cellClassName === 'function'
                                ? col.cellClassName(row, index)
                                : col.cellClassName
                            )}
                            // min-width: 0 lets flex/truncated content shrink inside the fixed cell
                            style={{minWidth: 0, ...edgeCellStyle(positionOf(colIndex), totalColumns)}}
                          >
                            {renderCell(col, row, index)}
                          </td>
                        ))}
                      </tr>
                      {expansion && (
                        <tr>
                          <td colSpan={totalColumns} style={{padding: 0, borderTop: 'none'}}>
                            {expansion}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={totalColumns}>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                      {loading ? '' : emptyMessage}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {loading && <LoadingOverlay />}
      </div>

      {pagination && (
        <CommonPagination
          currentPage={pagination.page}
          pageSize={pagination.limit}
          totalItems={pagination.totalItems}
          totalPages={Math.max(1, Math.ceil(pagination.totalItems / pagination.limit))}
          onPageChange={(_event: any, page: number) => pagination.onPageChange(page)}
          onPageSizeChange={(event: any) => pagination.onLimitChange(Number(event.target.value))}
          title={pagination.title ?? 'Records'}
        />
      )}
    </>
  )
}

export {Table}
export type {TableProps}
